import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { FaUser, FaPhone } from "react-icons/fa";
import Button from "@/components/common/Button";
import { createOrUpdateUserProfile } from "@/services/clerkUserService";
import toastService from "@/utils/toastService";

/**
 * Componente para completar datos de perfil después del registro con Clerk
 * Solo solicita datos que no existan en el perfil de Clerk
 */
const CompleteProfile = () => {
  const { user, isLoaded } = useUser();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: ""
  });
  
  const [loading, setLoading] = useState(false);
  const [dataComplete, setDataComplete] = useState(false);
  
  // Cargar datos existentes del usuario cuando Clerk está listo
  useEffect(() => {
    if (isLoaded && user) {
      const newFormData = {
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phone: user.publicMetadata.phone || ""
      };
      
      setFormData(newFormData);
      
      // Verificar si ya tenemos todos los datos necesarios
      checkIfDataComplete(newFormData);
    }
  }, [isLoaded, user]);
  
  // Verificar si ya tenemos todos los datos necesarios
  const checkIfDataComplete = (data) => {
    if (data.firstName && data.lastName && data.phone) {
      setDataComplete(true);
    } else {
      setDataComplete(false);
    }
  };
  
  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = {
      ...formData,
      [name]: value
    };
    
    setFormData(updatedData);
    checkIfDataComplete(updatedData);
  };
  
  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!dataComplete) {
      toastService.warning("Por favor completa todos los campos requeridos");
      return;
    }
    
    setLoading(true);
    
    try {
      // Actualizar datos básicos en Clerk
      await user.update({
        firstName: formData.firstName,
        lastName: formData.lastName
      });
      
      // Nota: El teléfono se guardará en Supabase, no en Clerk
      // Los metadatos públicos de Clerk requieren configuración especial
      
      // Crear o actualizar perfil en Supabase
      await createOrUpdateUserProfile({
        clerkId: user.id,
        email: user.primaryEmailAddress.emailAddress,
        nombre: `${formData.firstName} ${formData.lastName}`,
        telefono: formData.phone,
        tipoUsuario: user.primaryEmailAddress.emailAddress.endsWith('@taller.cl') ? 'Admin' : 'Cliente'
      });
      
      toastService.success("Perfil actualizado correctamente");
      
      // Redirigir a la página principal
      navigate('/');
    } catch (error) {
      console.error("Error al actualizar perfil:", error);
      toastService.error("Error al actualizar el perfil. Por favor, inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };
  
  // Si Clerk aún no ha cargado, mostrar spinner
  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-gray-800 rounded-xl shadow-lg p-8 border border-gray-700">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-white">Completa tu perfil</h2>
          <p className="mt-2 text-sm text-gray-400">
            Para continuar, necesitamos algunos datos adicionales
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md space-y-4">
            {/* Nombre */}
            {!user.firstName && (
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-1">
                  Nombre
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="h-5 w-5 text-gray-500" />
                  </div>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="appearance-none rounded-md relative block w-full px-3 py-3 pl-10 border border-gray-600 placeholder-gray-500 text-white bg-gray-700 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10"
                    placeholder="Nombre"
                  />
                </div>
              </div>
            )}
            
            {/* Apellido */}
            {!user.lastName && (
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-1">
                  Apellido
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="h-5 w-5 text-gray-500" />
                  </div>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="appearance-none rounded-md relative block w-full px-3 py-3 pl-10 border border-gray-600 placeholder-gray-500 text-white bg-gray-700 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10"
                    placeholder="Apellido"
                  />
                </div>
              </div>
            )}
            
            {/* Teléfono - siempre requerido */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-1">
                Teléfono
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaPhone className="h-5 w-5 text-gray-500" />
                </div>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="appearance-none rounded-md relative block w-full px-3 py-3 pl-10 border border-gray-600 placeholder-gray-500 text-white bg-gray-700 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10"
                  placeholder="+56 9 XXXX XXXX"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Formato: +56 9 XXXX XXXX
              </p>
            </div>
          </div>
          
          <div>
            <Button
              type="submit"
              disabled={loading || !dataComplete}
              fullWidth
              variant="primary"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Guardando...
                </span>
              ) : "Continuar"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompleteProfile;